import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import * as Yup from 'yup';
import CardHeader from 'react/shared/components/true_link/lab/form/wizard/CardHeader';
import CardText from 'react/shared/components/true_link/lab/form/wizard/CardText';
import NextPageButton from 'react/shared/components/true_link/lab/form/wizard/NextPageButton';
import TrueLinkLink from 'react/shared/components/true_link/main/TrueLinkLink';
import TrueLinkTooltip from 'react/shared/components/true_link/main/TrueLinkTooltip';
import TrueLinkTextInput from 'react/shared/components/true_link/main/form/TrueLinkTextInput';
import theme, { mediumBreakpointMediaQuery } from 'react/shared/theme/Theme';
import PALETTE from 'react/shared/theme/palette';
import schema from 'react/shared/utils/schemas';
import api from 'react/signups/card/api';
import trackUserData from 'react/signups/card/common/MarketoTracker';
import WizardCard from 'react/signups/card/common/WizardCard';

const useStyles = makeStyles(() => ({
  existingUser: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
    borderRadius: '6px',
    backgroundColor: PALETTE.blueLight,
    color: PALETTE.blue,

    '& a.primary': {
      padding: '8px 16px',
      background: PALETTE.blue,
      margin: '0',
      borderRadius: '3px',
      '&:hover': {
        background: PALETTE.blueDark,
      },
    },
  },
  link: {
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      '&.MuiTypography-root': {
        marginTop: theme.spacing(4),
      },
    },
  },
  tooltipText: {
    marginTop: '45px',
    color: PALETTE.grey2,
    [theme.breakpoints.down('lg')]: {
      marginTop: '10px',
    },
  },
  tooltipInnerText: {
    fontSize: '16px',
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',

    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      paddingBottom: '10px',

      '& button': {
        width: '100%',
        padding: '10px',
        marginBottom: '10px',
      },
    },
  },
}));
export default function Entry({ emailData, setCardAccountSignup, setEmailData, setUserTypeData }) {
  const classes = useStyles();
  const desktop = useMediaQuery(mediumBreakpointMediaQuery);
  const [credentials, setCredentials] = useState({
    email: emailData || '',
  });
  const [existingUser, setExistingUser] = useState(false);

  const validationSchema = Yup.object().shape({
    email: schema.email.required(),
  });

  const tooltipContent = (
    <div className={classes.tooltipInnerText}>
      <p>Your email will be used as your login once you’ve created your account. </p>
      <p>
        We’ll also keep you updated on the latest about True Link. We promise to never share your
        email.
      </p>
    </div>
  );

  const handleContinue = (values) => {
    trackUserData({
      Email: values.email,
    });
    const params = {
      data: {
        type: 'card_account_signup',
        attributes: {
          email: values.email,
        },
      },
    };
    setCredentials({
      email: values.email,
    });

    api
      .createEmailSignup(params)
      .then((response) => {
        const cardAccountSignup = response.data.data;
        const { attributes, id } = cardAccountSignup;
        const relationships = {
          account: null,
          card_account_trust: null,
          organization: null,
          user: null,
        };
        setExistingUser(false);
        setCardAccountSignup({ id, relationships, userType: '', ...attributes });
        setEmailData(attributes.email);
        setUserTypeData({ userType: 'family' });
      })

      .catch((error) => {
        if (error?.response?.status === 422) {
          const emailTakenError = error.response.data?.errors?.some(
            (error) => error.code === 'user_email_taken',
          );
          if (emailTakenError) {
            setExistingUser(true);
          }
        }
      });
  };

  return (
    <>
      <WizardCard showBackButton={false}>
        <CardHeader>Let's start with your email address</CardHeader>
        <Formik
          initialValues={credentials}
          onSubmit={handleContinue}
          validationSchema={validationSchema}
        >
          {({ handleChange, handleSubmit, setFieldTouched, values }) => (
            <>
              <TrueLinkTextInput
                labelText="Your email"
                name="email"
                onChange={handleChange}
                placeholder="Your email"
                required
                setFieldTouched={setFieldTouched}
                value={values.email}
              />
              {existingUser && (
                <div className={classes.existingUser}>
                  <span>This email is already in use</span>
                  <TrueLinkLink
                    className="primary"
                    href={RailsRoutes.sign_in_path()}
                    id="email-first-login-btn"
                  >
                    Log in
                  </TrueLinkLink>
                </div>
              )}
              <div className={classes.footer}>
                <NextPageButton id="email-first-continue-btn" onClick={handleSubmit} />
                <div className={classes.tooltipText}>
                  <TrueLinkTooltip
                    placement={desktop ? 'right' : 'bottom'}
                    tooltipContent={tooltipContent}
                  >
                    <span id={'tooltipEmailContent'}>Why we need this </span>
                    <i className="fa-light fa-circle-question" />
                  </TrueLinkTooltip>
                </div>
              </div>
            </>
          )}
        </Formik>
      </WizardCard>
      <CardText className={classes.link} variant="body2">
        Already have a True Link Card?{' '}
        <TrueLinkLink href={RailsRoutes.sign_in_path()}>Log in here.</TrueLinkLink>
      </CardText>
      <CardText className={classes.link} variant="body2">
        True Link{' '}
        <TrueLinkLink href="/legal/privacy-policy" target="_blank">
          Privacy Policy
        </TrueLinkLink>
        {' and '}
        <TrueLinkLink href="/legal/terms-of-service" target="_blank">
          Terms of Service
        </TrueLinkLink>
      </CardText>{' '}
    </>
  );
}

Entry.propTypes = {
  emailData: PropTypes.string,
  setCardAccountSignup: PropTypes.func.isRequired,
  setEmailData: PropTypes.func.isRequired,
  setUserTypeData: PropTypes.func.isRequired,
};
